/* eslint-disable no-magic-numbers */
import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button, Preloader } from "nlib/ui";
import { TABS as EDIT_BUSINESS_SETTINGS_TABS } from "nlib/windows/EditBusinessSettingsWindow";
import { getAccountsData } from "selectors/accounts";
import { getAuditData } from "selectors/audit";
import { getContactsData } from "selectors/contacts";
import { getSelectedBusinessData, getSelectedBusinessId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getTransactionsData } from "selectors/transactions";
import { useDispatch, useSelector } from "react-redux";
import ActivityEvents from "const/ActivityEvents";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import CategorizationFlows from "const/CategorizationFlows";
import ConfidenceAppend from "nlib/common/ConfidenceAppend";
import DateTimeFormats from "const/DateTimeFormats";
import GptAiExcludeRules from "const/GptAiExcludeRules";
import NoDataContent from "nlib/common/NoDataContent";
import React, { useCallback, useEffect, useMemo } from "react";
import SideBar, { SideBarContent, SideBarHeader } from "nlib/common/SideBar";
import TableRow from "./lib/TableRow";
import Texts from "utils/Texts";
import TransactionAmount from "nlib/common/TransactionAmount";
import Transactions from "utils/Transactions";
import UiActions from "actions/UiActions";
import classNames from "classnames";
import moment from "moment";
import useActivityInfo from "hooks/useActivityInfo";
import useDocument from "hooks/useDocument";
import useEnvVars from "hooks/useEnvVars";

const ENTITIES = [
  {
    key: "category",
    icon: Icons.SquaresFour
  },
  {
    key: "class",
    icon: Icons.Stack
  },
  {
    key: "location",
    icon: Icons.MapPin
  },
  {
    key: "project",
    icon: Icons.Flag
  }
];

const RpaDetailsPanel = ({ audit = false }) => {
  const dispatch = useDispatch();

  const [{ transactionId }, setEnvVars] = useEnvVars();

  const { uiTexts, messages } = useSelector(getTextsData);

  const accountsData = useSelector(getAccountsData);

  const contactsData = useSelector(getContactsData);

  const transactionsData = useSelector(getTransactionsData);

  const auditData = useSelector(getAuditData);

  const businessId = useSelector(getSelectedBusinessId);

  const {
    settings: { advancedDocumentsWorkflow } = {}
  } = useSelector(getSelectedBusinessData);

  const transactionData = useMemo(() => {
    return audit ? auditData.find(({ id }) => id === transactionId) : transactionsData.find(({ id }) => id === transactionId);
  }, [audit, auditData, transactionsData, transactionId]);

  const {
    type,
    amount,
    address,
    currency,
    vendorId,
    accountId,
    timestamp,
    documentId,
    description: bankDescription,
    account = accountsData.find(({ id }) => id === accountId),
    extraData: { gptAiCategory = "", gptAiDescription, gptAiReason } = {}
  } = transactionData || {};

  const advancedType = Transactions.getTransactionAdvancedType(transactionData);

  const addressData = contactsData.find(({ id }) => id === vendorId) || address;

  const seeTheDocument = !!documentId && advancedDocumentsWorkflow;

  const {
    result: activityCategorizationResult,
    fetching: activityFetching
  } = useActivityInfo({
    transactionId,
    fetchCondition: transactionId && !seeTheDocument,
    described: true,
    types: [
      ActivityEvents.TYPES.TRANSACTIONS_AUTO_APPROVED,
      ActivityEvents.TYPES.TRANSACTIONS_AUTO_CATEGORIZED
    ]
  });

  const {
    result: activityPairedWithDocumentResult,
    fetching: activityPairedWithDocumentFetching
  } = useActivityInfo({
    transactionId,
    fetchCondition: transactionId && seeTheDocument,
    types: [
      ActivityEvents.TYPES.TRANSACTIONS_AUTO_PAIRED,
      ActivityEvents.TYPES.TRANSACTIONS_AUTO_PAIRED_AND_APPROVED
    ]
  });

  const { result: documentResult, fetching: documentFetching } = useDocument({
    documentId,
    fetchCondition: seeTheDocument
  });

  const { matchingTransactions, confidences = {}, categorizedByFlow } = activityCategorizationResult || {};

  const excludeRule = gptAiCategory.startsWith("EXCLUDE_RULE");

  const excluded = !activityFetching && !categorizedByFlow && (excludeRule || advancedType === AdvancedTransactionTypes.TRANSFER);

  const showReason = categorizedByFlow === "ai" && !!gptAiReason && !excludeRule;

  const [
    hardExcludeText,
    noBankDescriptionText,
    inProgressText,
    softExcludePaymentSystemText,
    softExcludePayrollSystemText,
    softExcludeAtmWithdrawalText,
    softExcludeCheckText,
    softExcludeTransfersText,
    softExcludeExpenseReimbursementsText,
    softExcludeMarketplacesText,
    softExcludePersonToPersonPaymentsText,
    softExcludeUnclearText,
    softExcludeCustomText,
    categorizedByRuleText,
    categorizedByReasonText,
    categorizedByDescriptionText,
    categorizedByVendorText,
    categorizedByAiText,
    noCategorizationDetailsText,
    documentAutoPairedTitle,
    documentPairedTitle,
    documentAutoPairedText,
    documentPairedText,
    adjustCategorizationTitle,
    adjustCategorizationDescription
  ] = messages.rpaDetails;

  const handleCloseClick = useCallback(() => {
    setEnvVars({ transactionId: null });
  }, [setEnvVars]);

  const gptDescription = useMemo(() => {
    if (activityFetching || categorizedByFlow) return null;
    if (!bankDescription) return noBankDescriptionText;
    if (advancedType === AdvancedTransactionTypes.TRANSFER) return softExcludeTransfersText;
    if ([
      AdvancedTransactionTypes.PAYMENT,
      AdvancedTransactionTypes.BILL_PAYMENT,
      AdvancedTransactionTypes.SALES_RECEIPT,
      AdvancedTransactionTypes.REFUND_RECEIPT
    ].includes(advancedType)) {
      return Texts.replaceVars(hardExcludeText, { type: uiTexts[advancedType] });
    }
    if (!gptAiCategory) {
      return inProgressText;
    }
    if (Object.values(GptAiExcludeRules).includes(gptAiCategory)) {
      switch (gptAiCategory) {
        case GptAiExcludeRules.PAYMENT_SYSTEM:
          return softExcludePaymentSystemText;
        case GptAiExcludeRules.PAYROLL_SYSTEM:
          return softExcludePayrollSystemText;
        case GptAiExcludeRules.ATM_WITHDRAWAL:
          return softExcludeAtmWithdrawalText;
        case GptAiExcludeRules.CHECK:
          return softExcludeCheckText;
        case GptAiExcludeRules.TRANSFER:
          return softExcludeTransfersText;
        case GptAiExcludeRules.EXPENSE_REIMBURSEMENT:
          return softExcludeExpenseReimbursementsText;
        case GptAiExcludeRules.MARKETPLACE:
          return softExcludeMarketplacesText;
        case GptAiExcludeRules.PERSON_TO_PERSON_PAYMENT:
          return softExcludePersonToPersonPaymentsText;
        case GptAiExcludeRules.UNCLEAR:
          return softExcludeUnclearText;
        case GptAiExcludeRules.CUSTOM:
          return softExcludeCustomText;
      }
    }

    return gptAiDescription;
  }, [
    uiTexts,
    advancedType,
    gptAiCategory,
    gptAiDescription,
    inProgressText,
    bankDescription,
    hardExcludeText,
    activityFetching,
    categorizedByFlow,
    noBankDescriptionText,
    softExcludePaymentSystemText,
    softExcludePayrollSystemText,
    softExcludeAtmWithdrawalText,
    softExcludeCheckText,
    softExcludeUnclearText,
    softExcludeTransfersText,
    softExcludeExpenseReimbursementsText,
    softExcludeMarketplacesText,
    softExcludePersonToPersonPaymentsText,
    softExcludeCustomText
  ]);

  const categorizedByFlowText = useMemo(() => {
    switch (categorizedByFlow) {
      case CategorizationFlows.RULE:
        return categorizedByRuleText;
      case CategorizationFlows.REASON:
        return categorizedByReasonText;
      case CategorizationFlows.DESCRIPTION:
        return categorizedByDescriptionText;
      case CategorizationFlows.VENDOR:
        return categorizedByVendorText;
      default:
        return categorizedByAiText;
    }
  }, [
    categorizedByFlow,
    categorizedByRuleText,
    categorizedByReasonText,
    categorizedByDescriptionText,
    categorizedByVendorText,
    categorizedByAiText
  ]);

  const handleDocumentEdit = useCallback(() => {
    dispatch(UiActions.showEditDocumentWindow({ documentId }));
  }, [dispatch, documentId]);

  const handleBusinessSettingsClick = useCallback(() => {
    dispatch(UiActions.showEditBusinessSettingsWindow({
      businessId,
      defaultActiveTab: EDIT_BUSINESS_SETTINGS_TABS.AI_BOOKKEEPER_TAB_PROMPTS,
      extraData: {
        gptAiCategory: advancedType === AdvancedTransactionTypes.TRANSFER ? GptAiExcludeRules.TRANSFER : gptAiCategory
      }
    }));
  }, [dispatch, businessId, gptAiCategory, advancedType]);

  useEffect(() => {
    if (!transactionData) {
      setEnvVars({ transactionId: null }, null, true);
    }
  }, [transactionData, setEnvVars]);
  if (!transactionData) {
    return (
      <SideBar className={Css.rpaDetailsPanel}>
        <SideBarHeader onCloseClick={handleCloseClick} />
        <SideBarContent>
          <Preloader />
        </SideBarContent>
      </SideBar>
    );
  }
  if (seeTheDocument) {
    return (
      <SideBar className={Css.rpaDetailsPanel}>
        <SideBarHeader className={Css.sideBarHeader} onCloseClick={handleCloseClick}>
          <TransactionAmount
            className={Css.amount}
            amount={amount}
            type={type}
            currency={currency} />
        </SideBarHeader>
        <SideBarContent>
          <div className={Css.info}>
            <Icons.CalendarBlank />
            <span>{moment.utc(timestamp).format(DateTimeFormats.DATE_TEXT)}</span>
          </div>
          {!!account?.name && (
            <div className={Css.account}>
              <Icons.CreditCard />
              <span>{account?.name}</span>
            </div>
          )}
          <div className={Css.payee}>
            <div className={Css.title}>
              <span>{uiTexts.payeeDetails}</span>
            </div>
            <div className={Css.description}>
              <Icons.FileText />
              <span className={Css.muted}>{uiTexts.seeTheDocument}</span>
            </div>
          </div>
          <div className={Css.title}>{uiTexts.bankStatementDetails}</div>
          <div className={Css.description} title={bankDescription}>
            {bankDescription ? <Icons.Info /> : <Icons.Warning className={Css.warning} />}
            <span className={classNames(!bankDescription && Css.muted)}>
              {bankDescription || `${uiTexts.noBankDescriptionOrMemo} :(`}
            </span>
          </div>
          {(activityPairedWithDocumentFetching || documentFetching)
            ? <Preloader />
            : (
              <>
                <div className={Css.title}>
                  {activityPairedWithDocumentResult ? documentAutoPairedTitle : documentPairedTitle}
                </div>
                <div className={Css.description}>
                  <Icons.FileText />
                  <span>
                    {activityPairedWithDocumentResult ? documentAutoPairedText : documentPairedText}
                  </span>
                </div>
                <div className={Css.entity}>
                  <Button
                    outline
                    icon={Icons.FileText}
                    className={Css.openDocumentButton}
                    onClick={handleDocumentEdit}>
                    {uiTexts.openDocument}
                  </Button>
                  <div className={Css.title}>{uiTexts.document}</div>
                  <div className={Css.table}>
                    <TableRow header />
                    <TableRow document={documentResult} />
                  </div>
                </div>
              </>
            )}
        </SideBarContent>
      </SideBar>
    );
  }

  return (
    <SideBar className={Css.rpaDetailsPanel}>
      <SideBarHeader className={Css.sideBarHeader} onCloseClick={handleCloseClick}>
        <TransactionAmount
          className={Css.amount}
          amount={amount}
          type={type}
          currency={currency} />
      </SideBarHeader>
      <SideBarContent>
        <div className={Css.info}>
          <Icons.CalendarBlank />
          <span>{moment.utc(timestamp).format(DateTimeFormats.DATE_TEXT)}</span>
        </div>
        {!!account?.name && (
          <div className={Css.account}>
            <Icons.CreditCard />
            <span>{account?.name}</span>
          </div>
        )}
        {!!address?.name && (
          <div className={Css.payee}>
            <div className={Css.title}>
              <span>{uiTexts.payeeDetails}</span>
            </div>
            <div className={Css.description}>
              <Icons.AddressBook />
              <span>{address?.name}</span>
              {!!addressData?.extraData?.gptAiDescription && (
                <>
                  <span> - </span>
                  <span>{addressData?.extraData?.gptAiDescription}</span>
                </>
              )}
            </div>
          </div>
        )}
        <div className={Css.title}>{uiTexts.bankStatementDetails}</div>
        <div className={Css.description} title={bankDescription}>
          {bankDescription ? <Icons.Info /> : <Icons.Warning className={Css.warning} />}
          <span className={classNames(!bankDescription && Css.muted)}>
            {bankDescription || `${uiTexts.noBankDescriptionOrMemo} :(`}
          </span>
        </div>

        {!!gptDescription && (
          <>
            <div className={Css.title}>{uiTexts.aiDescription}</div>
            <div className={Css.description}>
              <Icons.Cpu />
              <span>{gptDescription}</span>
            </div>
          </>
        )}
        {showReason && (
          <>
            <div className={Css.title}>{uiTexts.whyTheAISuggestedThis}</div>
            <div className={Css.description}>
              <Icons.Chat />
              <span>{gptAiReason}</span>
            </div>
          </>
        )}
        {excluded && (
          <>
            <div className={Css.title}>{adjustCategorizationTitle}</div>
            <div className={Css.description}>
              <Icons.Gear />
              <span>{adjustCategorizationDescription}</span>
            </div>
            <div className={Css.title}>
              <Button
                outline
                onClick={handleBusinessSettingsClick}>
                {uiTexts.aiCustomizationSettings}
              </Button>
            </div>
          </>
        )}
        {categorizedByFlow
          ? (
            <>
              <div className={Css.title}>{uiTexts.categorizedByAiBookkeeper}</div>
              <div className={Css.description}>
                <Icons.SquaresFour />
                <span>{categorizedByFlowText}</span>
              </div>
              {!!matchingTransactions && ENTITIES.map(({ key: entityKey, icon: Icon }) => {
                return !!matchingTransactions[entityKey]?.length && (
                  <div key={entityKey} className={Css.entity}>
                    <div className={Css.value}>
                      <Icon data-tooltip={uiTexts[entityKey]} />
                      <div className={Css.text} title={activityCategorizationResult[entityKey]}>
                        {activityCategorizationResult[entityKey]}
                      </div>
                      <ConfidenceAppend
                        className={Css.confidence}
                        confidence={confidences[entityKey] || transactionData[entityKey]?.confidence} />
                    </div>
                    <div className={Css.title}>{uiTexts.similarTransactions}</div>
                    <div className={Css.table}>
                      <TableRow entityKey={entityKey} header />
                      {matchingTransactions[entityKey].map((item, index) => (
                        <TableRow key={item.id || index} entityKey={entityKey} item={item} currency={currency} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )
          : (
            activityFetching ? <Preloader /> : (!excludeRule && (
              <NoDataContent className={Css.noData} icon={Icons.SquaresFour}>
                <div className={Css.emptyText}>{noCategorizationDetailsText}</div>
              </NoDataContent>
            ))
          )}
      </SideBarContent>
    </SideBar>
  );
};

export default React.memo(RpaDetailsPanel);
